import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './ScenarioEditMessagePopUp.module.css';
import { copyIcon, newChatIcon, rewindIcon, editIcon } from '../../assets/icons';

interface ScenarioEditMessagePopUpProps {
  position: { x: number, y: number };
  onClose: () => void;
  onCopy: () => void;
  onNewChat: () => void;
  onRewind: () => void;
  onEdit: () => void;
  onRegenerate?: () => void;
  isLatestMessage?: boolean;
  isUserMessage?: boolean;
}

const ScenarioEditMessagePopUp: React.FC<ScenarioEditMessagePopUpProps> = ({
  position,
  onClose,
  onCopy,
  onNewChat,
  onRewind,
  onEdit,
  onRegenerate,
  isLatestMessage = false,
  isUserMessage = false
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  // Handle click outside and scroll to close the popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Close popup on scroll
    const handleScroll = () => {
      onClose();
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scroll events

    // Clean up event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);

  // Create the popup content
  const popupContent = (
    <div className={styles.popupOverlay}>
      <div 
        ref={popupRef} 
        className={styles.popup} 
        style={{
          top: `${position.y}px`,
          left: `${position.x}px`,
        }}
      >
        <button 
          className={styles.popupItem} 
          onClick={(e) => { e.stopPropagation(); onCopy(); }}
        >
          <img src={copyIcon} alt="Copy" className={styles.popupIcon} />
          <span>Copy</span>
        </button>
        
        <button 
          className={styles.popupItem} 
          onClick={(e) => { e.stopPropagation(); onNewChat(); }}
        >
          <img src={newChatIcon} alt="New chat" className={styles.popupIcon} />
          <span>New scenario from here</span>
        </button>
        
        {/* Always show Rewind for user messages and non-latest messages */}
        {(!isLatestMessage || isUserMessage) && (
          <button 
            className={styles.popupItem} 
            onClick={(e) => { e.stopPropagation(); onRewind(); }}
          >
            <img src={rewindIcon} alt="Rewind" className={styles.popupIcon} />
            <span>Rewind to here</span>
          </button>
        )}
        
        <button 
          className={styles.popupItem} 
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
        >
          <img src={editIcon} alt="Edit" className={styles.popupIcon} />
          <span>Edit message</span>
        </button>
        
        {/* Show Regenerate only for latest non-user message */}
        {isLatestMessage && !isUserMessage && (
          <button 
            className={styles.popupItem} 
            onClick={(e) => { e.stopPropagation(); onRegenerate && onRegenerate(); }}
          >
            <img src={rewindIcon} alt="Regenerate" className={styles.popupIcon} />
            <span>Regenerate message</span>
          </button>
        )}
      </div>
    </div>
  );

  // Use React Portal to render the popup directly in the document body
  return ReactDOM.createPortal(
    popupContent,
    document.body
  );
};

export default ScenarioEditMessagePopUp; 